import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import {
  APP_BUILD_AT,
  APP_BUILD_VERSION,
} from "../generatedBuildVersion";

const DIAGNOSTIC_ENDPOINT =
  "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/clientDiagnostics";
const DIAGNOSTIC_SCHEMA_VERSION = "2026-10-15.2";
const SESSION_KEY = "cinema.admin.diagnosticSession";
const QUEUE_KEY = "cinema.admin.diagnosticQueue.v2";
const LIFECYCLE_KEY = "cinema.admin.lastLifecycle.v2";
const MAX_QUEUE_LENGTH = 120;
const MAX_FLUSH_PER_RUN = 40;
let memoryQueue = [];
let flushPromise = null;

const createId = (prefix) => {
  const randomPart =
    typeof window.crypto?.randomUUID === "function"
      ? window.crypto.randomUUID().replace(/-/g, "").slice(0, 12)
      : Math.random().toString(36).slice(2, 14);
  return `${prefix}-${randomPart}`;
};

const getSessionId = () => {
  try {
    const current = window.sessionStorage.getItem(SESSION_KEY);
    if (current) return current;

    const next = createId("diag");
    window.sessionStorage.setItem(SESSION_KEY, next);
    return next;
  } catch (_) {
    return `diag-${Date.now().toString(36)}`;
  }
};

const sanitizeDetail = (detail) =>
  Object.fromEntries(
    Object.entries(detail || {})
      .slice(0, 20)
      .map(([key, value]) => {
        if (typeof value === "boolean" || typeof value === "number") {
          return [key, value];
        }
        return [key, String(value ?? "").slice(0, 120)];
      }),
  );

const getDisplayMode = () => {
  if (window.matchMedia?.("(display-mode: standalone)")?.matches) {
    return "standalone";
  }
  if (window.navigator.standalone === true) return "ios-standalone";
  if (window.matchMedia?.("(display-mode: fullscreen)")?.matches) {
    return "fullscreen";
  }
  return "browser";
};

const getNavigationType = () => {
  try {
    return (
      window.performance?.getEntriesByType?.("navigation")?.[0]?.type ||
      "unknown"
    );
  } catch (_) {
    return "unknown";
  }
};

const readQueue = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(QUEUE_KEY) || "[]");
    if (Array.isArray(stored)) return stored;
  } catch (_) {
    // Fall back to the in-memory queue when storage is unavailable/corrupt.
  }
  return memoryQueue;
};

const writeQueue = (queue) => {
  const limitedQueue = queue.slice(-MAX_QUEUE_LENGTH);
  memoryQueue = limitedQueue;
  try {
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(limitedQueue));
  } catch (_) {
    // The in-memory copy still allows diagnostics during this page session.
  }
};

const enqueue = (event, detail) => {
  const displayMode = getDisplayMode();
  const entry = {
    eventId: createId("evt"),
    event,
    sessionId: getSessionId(),
    schemaVersion: DIAGNOSTIC_SCHEMA_VERSION,
    clientAt: new Date().toISOString(),
    route: window.location.pathname,
    visibility: document.visibilityState,
    standalone:
      displayMode === "standalone" || displayMode === "ios-standalone",
    displayMode,
    online: navigator.onLine !== false,
    navigationType: getNavigationType(),
    appBuildVersion: APP_BUILD_VERSION,
    appBuildAt: APP_BUILD_AT,
    userAgent: navigator.userAgent,
    detail: sanitizeDetail(detail),
  };

  writeQueue([...readQueue(), entry]);
  return entry;
};

export const flushAdminDiagnostics = async () => {
  if (flushPromise) return flushPromise;

  flushPromise = (async () => {
    const user = auth.currentUser;
    if (!user || navigator.onLine === false) return;

    let sentCount = 0;
    while (sentCount < MAX_FLUSH_PER_RUN) {
      const queue = readQueue();
      const entry = queue[0];
      if (!entry) break;

      const token = await user.getIdToken();
      const response = await fetch(DIAGNOSTIC_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(entry),
        cache: "no-store",
        keepalive: true,
      });

      if (!response.ok) {
        throw new Error(`Diagnostic upload failed (${response.status})`);
      }

      const currentQueue = readQueue();
      writeQueue(
        currentQueue[0]?.eventId === entry.eventId
          ? currentQueue.slice(1)
          : currentQueue.filter((item) => item.eventId !== entry.eventId),
      );
      sentCount += 1;
    }
  })()
    .catch(() => {
      // Keep unsent entries on-device for the next resume/reload/online event.
    })
    .finally(() => {
      flushPromise = null;
    });

  return flushPromise;
};

export const logAdminDiagnostic = (event, detail = {}) => {
  // Persist first. If iOS suspends the process before fetch completes, the
  // next app launch sends this event from localStorage.
  enqueue(event, detail);
  void flushAdminDiagnostics();
};

const readLastLifecycle = () => {
  try {
    return JSON.parse(window.localStorage.getItem(LIFECYCLE_KEY) || "null");
  } catch (_) {
    return null;
  }
};

const recordLifecycle = (phase, extra = {}) => {
  const previous = readLastLifecycle();
  const now = new Date().toISOString();

  try {
    window.localStorage.setItem(
      LIFECYCLE_KEY,
      JSON.stringify({ phase, at: now, visibility: document.visibilityState }),
    );
  } catch (_) {
    // Lifecycle logging remains best-effort when storage is unavailable.
  }

  logAdminDiagnostic("pwa-lifecycle", {
    phase,
    previousPhase: previous?.phase || "none",
    previousAt: previous?.at || "none",
    queuedCount: readQueue().length,
    ...extra,
  });
};

if (typeof window !== "undefined") {
  recordLifecycle("script-init");

  document.addEventListener("visibilitychange", () => {
    recordLifecycle(`visibility-${document.visibilityState}`);
  });
  window.addEventListener("pageshow", (event) => {
    recordLifecycle("pageshow", { persisted: event.persisted === true });
  });
  window.addEventListener("pagehide", (event) => {
    recordLifecycle("pagehide", { persisted: event.persisted === true });
  });
  window.addEventListener("focus", () => recordLifecycle("focus"));
  window.addEventListener("blur", () => recordLifecycle("blur"));
  window.addEventListener("online", () => {
    recordLifecycle("online");
    void flushAdminDiagnostics();
  });
  window.addEventListener("offline", () => recordLifecycle("offline"));
  window.addEventListener("freeze", () => recordLifecycle("freeze"));
  window.addEventListener("resume", () => recordLifecycle("resume"));
  window.addEventListener("cinema:resume", () => {
    recordLifecycle("cinema-resume");
    void flushAdminDiagnostics();
  });

  onAuthStateChanged(auth, (user) => {
    if (user) void flushAdminDiagnostics();
  });

  window.setInterval(() => {
    if (document.visibilityState === "visible") void flushAdminDiagnostics();
  }, 30 * 1000);
}
